import { useState } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle2, Navigation } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatError } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const OFFICE_ADDRESS = "Jl. Yos Sudarso RT. 16 No. 28, Selumit, Tarakan Tengah, Tarakan, Kalimantan Utara";
const MAP_QUERY = encodeURIComponent(OFFICE_ADDRESS);
const MAP_EMBED_SRC = `https://www.google.com/maps?q=${MAP_QUERY}&z=16&output=embed`;
const MAP_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`;

const SERVICE_TYPES = [
  { value: "sistem-informasi", label: "Pengembangan Sistem Informasi" },
  { value: "pengadaan-perangkat-ti", label: "Pengadaan Perangkat TI" },
  { value: "konsultasi-ti", label: "Konsultasi TI" },
  { value: "infrastruktur-jaringan", label: "Infrastruktur Jaringan" },
  { value: "general", label: "Lainnya / Konsultasi Umum" },
];

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_type: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useSeo({
    title: "Hubungi Kami — Minta Penawaran",
    description:
      "Hubungi CV. Komunika Teknik untuk konsultasi dan penawaran layanan sistem informasi, pengadaan perangkat TI, dan infrastruktur. Respon < 24 jam.",
    path: "/kontak",
  });

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setSubmitted(true);
      toast.success("Permintaan terkirim. Tim kami akan menghubungi Anda.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service_type: "general",
        message: "",
      });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Gagal mengirim");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="contact-page">
      <Header />

      <section className="bg-[#0F172A] text-white pt-24 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 kt-grid-bg opacity-10" />
        <div className="kt-container relative">
          <div className="kt-eyebrow text-[#FCA5A5]">Hubungi Kami</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl">
            Mari diskusikan kebutuhan TI organisasi Anda.
          </h1>
          <p className="mt-6 text-zinc-300 max-w-2xl">
            Kirim ringkasan kebutuhan Anda. Tim kami akan merespon dalam 1×24
            jam hari kerja dengan estimasi awal dan jadwal konsultasi.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="kt-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-8">
            <div className="kt-card p-6 md:p-8">
              <div className="kt-eyebrow">Kontak Langsung</div>
              <h3 className="font-display text-xl font-bold mt-3">
                Tim Solusi & Pengadaan
              </h3>
              <div className="kt-divider-red mt-4" />
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#DC2626] shrink-0 mt-0.5" />
                  <span className="text-[#18181B]">
                    Jl. Yos Sudarso RT. 16 No. 28,
                    <br />
                    Selumit, Tarakan Tengah
                    <br />
                    Tarakan, Kalimantan Utara, Indonesia
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={20} className="text-[#DC2626] shrink-0" />
                  <a
                    href="mailto:info@komunikateknik.online"
                    className="text-[#18181B] hover:text-[#DC2626]"
                    data-testid="contact-info-email"
                  >
                    info@komunikateknik.online
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={20} className="text-[#DC2626] shrink-0" />
                  <a
                    href="tel:+6285246286550"
                    className="text-[#18181B] hover:text-[#DC2626]"
                    data-testid="contact-info-phone"
                  >
                    +62 852-4628-6550
                  </a>
                </li>
              </ul>
            </div>

            <div className="kt-card p-6 md:p-8 bg-[#0F172A] text-white border-[#0F172A]">
              <div className="kt-eyebrow text-[#FCA5A5]">Komitmen Layanan</div>
              <ul className="mt-6 space-y-3 text-sm">
                {[
                  "Respon < 24 jam pada hari kerja",
                  "Proposal & estimasi biaya transparan",
                  "Tim engineering bersertifikasi",
                  "Garansi & support purna jual",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-zinc-300">
                    <CheckCircle2 size={18} className="text-[#DC2626] mt-0.5" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="kt-card p-6 md:p-10"
              data-testid="contact-form"
            >
              <h2 className="font-display text-2xl md:text-3xl font-bold">
                Formulir Permintaan Penawaran
              </h2>
              <div className="kt-divider-red mt-4" />

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Nama Lengkap *" testId="contact-input-name">
                  <input
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    className={inputCls}
                    placeholder="John Doe"
                    data-testid="contact-input-name"
                  />
                </Field>
                <Field label="Email *" testId="contact-input-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    className={inputCls}
                    placeholder="john@perusahaan.co.id"
                    data-testid="contact-input-email"
                  />
                </Field>
                <Field label="No. Telepon">
                  <input
                    value={form.phone}
                    onChange={onChange("phone")}
                    className={inputCls}
                    placeholder="+62..."
                    data-testid="contact-input-phone"
                  />
                </Field>
                <Field label="Instansi / Perusahaan">
                  <input
                    value={form.company}
                    onChange={onChange("company")}
                    className={inputCls}
                    placeholder="PT / Dinas / BUMN"
                    data-testid="contact-input-company"
                  />
                </Field>
                <Field label="Layanan yang Diminati" className="md:col-span-2">
                  <select
                    value={form.service_type}
                    onChange={onChange("service_type")}
                    className={inputCls}
                    data-testid="contact-select-service"
                  >
                    {SERVICE_TYPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Pesan / Ruang Lingkup *" className="md:col-span-2">
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={onChange("message")}
                    className={inputCls}
                    placeholder="Jelaskan kebutuhan, skala, dan timeline yang diharapkan…"
                    data-testid="contact-input-message"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="kt-btn-primary mt-8 disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                {submitting ? "Mengirim…" : "Kirim Permintaan"} <Send size={16} />
              </button>

              {submitted && (
                <div
                  className="mt-6 p-4 border border-[#0369A1] bg-[#F0F9FF] text-[#0C4A6E] text-sm"
                  data-testid="contact-success-banner"
                >
                  Terima kasih! Permintaan Anda telah kami terima. Tim kami
                  akan menghubungi Anda dalam 1×24 jam kerja.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[#0F172A] text-white" data-testid="contact-map-section">
        <div className="kt-container py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="kt-eyebrow text-[#FCA5A5]">Lokasi Kantor</div>
              <h2 className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight">
                Kunjungi kantor pusat kami di Tarakan.
              </h2>
              <div className="kt-divider-red mt-5" />
              <div className="mt-6 flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                <MapPin size={18} className="text-[#DC2626] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-white">
                    CV. Komunika Teknik
                  </div>
                  <div className="mt-1">
                    Jl. Yos Sudarso RT. 16 No. 28
                    <br />
                    Selumit, Tarakan Tengah
                    <br />
                    Tarakan, Kalimantan Utara · Indonesia
                  </div>
                </div>
              </div>
              <a
                href={MAP_DIRECTIONS_URL}
                target="_blank"
                rel="noreferrer"
                className="kt-btn-primary mt-7"
                data-testid="contact-directions-button"
              >
                Petunjuk Arah <Navigation size={16} />
              </a>
              <div className="mt-6 text-xs font-mono-kt text-zinc-500">
                Buka: Senin–Jumat · 08.00 – 17.00 WITA
              </div>
            </div>

            <div className="lg:col-span-8">
              <div
                className="aspect-[16/10] border border-white/10 bg-[#1e293b] overflow-hidden"
                data-testid="contact-map-frame"
              >
                <iframe
                  title="Peta Lokasi CV. Komunika Teknik"
                  src={MAP_EMBED_SRC}
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: "grayscale(0.15) contrast(1.05)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const inputCls =
  "w-full border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0369A1] focus:ring-1 focus:ring-[#0369A1] transition-colors";

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#18181B] mb-2">
      {label}
    </span>
    {children}
  </label>
);

export default ContactPage;
